// Ethical Advertisement Banner Component
// Displays approved ads with strict ethical guidelines
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, ExternalLink, Info, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdBanner = ({ position = 'bottom', maxAds = 1 }) => {
  const [ads, setAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [showEthicsInfo, setShowEthicsInfo] = useState(false);

  useEffect(() => {
    fetchApprovedAds();
  }, []);

  // Rotate ads every 30 seconds
  useEffect(() => {
    if (ads.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentAdIndex((prev) => (prev + 1) % ads.length);
    }, 30000);

    return () => clearInterval(interval);
  }, [ads.length]);

  const fetchApprovedAds = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ads`);
      setAds(res.data.ads || []);
    } catch (error) {
      console.error('Error fetching ads:', error);
    }
  };

  const trackImpression = useCallback(async (adId) => {
    try {
      await axios.post(`${API_URL}/api/ads/${adId}/impression`);
    } catch (e) {}
  }, []);

  const trackClick = async (adId) => {
    try {
      await axios.post(`${API_URL}/api/ads/${adId}/click`);
    } catch (e) {}
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('drbooking_ad_dismissed', 'true');
  };

  const handleAdClick = (ad) => {
    trackClick(ad.id);
    if (ad.link_url) {
      window.open(ad.link_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Check if dismissed this session
  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('drbooking_ad_dismissed');
    if (wasDismissed) {
      setDismissed(true);
    }
  }, []);

  // Track impression when ad is displayed
  useEffect(() => {
    if (ads.length > 0 && !dismissed) {
      trackImpression(ads[currentAdIndex]?.id);
    }
  }, [currentAdIndex, ads, dismissed, trackImpression]);

  if (dismissed || ads.length === 0) return null;

  const currentAd = ads[currentAdIndex];
  if (!currentAd) return null;

  const positionClasses = {
    bottom: 'fixed bottom-0 left-0 right-0 z-40',
    top: 'fixed top-16 left-0 right-0 z-40',
    inline: 'relative w-full my-4'
  };

  return (
    <div className={`${positionClasses[position]} animate-slide-up`} data-testid="ad-banner">
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 border-t border-slate-700 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            {/* Ad Badge - Transparence obligatoire */}
            <div className="flex items-center gap-1 shrink-0">
              <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
                <Info className="w-3 h-3" />
                Publicité
              </span>
              <button
                onClick={() => setShowEthicsInfo(!showEthicsInfo)}
                className="text-slate-500 hover:text-slate-400 p-1"
                title="Politique éthique"
              >
                <Shield className="w-4 h-4" />
              </button>
            </div>

            {/* Ad Content */}
            <div 
              className="flex-1 flex items-center gap-4 cursor-pointer"
              onClick={() => handleAdClick(currentAd)}
            >
              {currentAd.image_url && (
                <img 
                  src={currentAd.image_url} 
                  alt={currentAd.title}
                  className="w-12 h-12 object-cover rounded-lg shrink-0"
                />
              )}
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{currentAd.category_icon || '📢'}</span>
                  <h4 className="text-white font-medium text-sm truncate">
                    {currentAd.title}
                  </h4>
                </div>
                <p className="text-slate-400 text-xs truncate">
                  {currentAd.description}
                </p>
                <p className="text-slate-500 text-xs">
                  Par {currentAd.advertiser_name}
                </p>
              </div>

              {currentAd.link_url && (
                <ExternalLink className="w-4 h-4 text-slate-400 shrink-0" />
              )}
            </div>

            {/* Ad Navigation (if multiple ads) */}
            {ads.length > 1 && (
              <div className="flex items-center gap-1 shrink-0">
                {ads.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentAdIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentAdIndex ? 'bg-blue-500' : 'bg-slate-600'
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Dismiss Button */}
            <button
              onClick={handleDismiss}
              className="text-slate-500 hover:text-white p-1 shrink-0"
              title="Fermer"
              data-testid="ad-dismiss-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Ethics Info Panel */}
          {showEthicsInfo && (
            <div className="mt-3 p-3 bg-slate-700/50 rounded-lg text-xs text-slate-400 border border-slate-600">
              <p className="font-medium text-slate-300 mb-2 flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-400" />
                Publicité Éthique Approuvée
              </p>
              <ul className="space-y-1 list-disc list-inside">
                <li>✅ Vérifiée et approuvée par l'administration drbooking</li>
                <li>✅ Respecte l'éthique médicale et la moralité</li>
                <li>✅ Conforme aux règles de pudeur et de décence</li>
                <li>✅ Sans contenus trompeurs ou discriminatoires</li>
                <li>✅ Annonceur identifié et vérifié</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdBanner;
